"use client";

import { useParams, usePathname, useSearchParams } from "next/navigation";
import { useCallback } from "react";

/**
 * Hook that returns a safe pathname (never null)
 * @returns A pathname string that is guaranteed not to be null
 */
export function useSafePathname(): string {
  const pathname = usePathname();
  return pathname ?? "/"; // Default to root if null
}

/**
 * Hook that returns safe URL search parameters (never null)
 * @returns Object with search parameter methods that handle null values
 */
export function useSafeSearchParams() {
  const searchParams = useSearchParams();

  // Define a function to get a search parameter value
  const getSearchParam = useCallback(
    (param: string): string | null => {
      return searchParams?.get(param) ?? null;
    },
    [searchParams]
  );

  // Define a function to create a new URLSearchParams object
  const getURLSearchParams = useCallback((): URLSearchParams => {
    return searchParams
      ? new URLSearchParams(searchParams)
      : new URLSearchParams();
  }, [searchParams]);

  return {
    get: getSearchParam,
    getAll: (param: string) => searchParams?.getAll(param) ?? [],
    has: (param: string) => searchParams?.has(param) ?? false,
    toString: () => searchParams?.toString() ?? "",
    entries: () => searchParams?.entries() ?? new URLSearchParams().entries(),
    forEach: (callback: (value: string, key: string) => void) => {
      searchParams?.forEach(callback) ??
        new URLSearchParams().forEach(callback);
    },
    toURLSearchParams: getURLSearchParams,
  };
}

/**
 * Hook that returns safe route parameters (never null)
 * @returns Parameters object that is guaranteed not to be null
 */
export function useSafeParams<
  T extends Record<string, string | string[]>,
>(): T {
  const params = useParams();
  return (params ?? {}) as T;
}

/**
 * Utility function to safely extract an ID from params
 * @param params The params object or the id directly
 * @param key Optional key if id is nested in params
 * @returns A string id or empty string if not found
 */
export function getSafeParamId(params: unknown, key = "id"): string {
  if (!params) return "";

  // If params is directly the id string
  if (typeof params === "string") return params;

  // If params is an object with the key
  if (typeof params === "object" && params !== null && key in params) {
    const value = (params as Record<string, unknown>)[key];

    // Handle array case
    if (Array.isArray(value)) {
      return value.length > 0 ? String(value[0]) : "";
    }

    // Handle string case
    return typeof value === "string" ? value : "";
  }

  return "";
}
